<template>
  <!-- scroll-mt keeps the sticky navbar clear of the heading when the
       expander scrolls this section back into view. -->
  <div ref="productShowcase" class="scroll-mt-[130px] md:scroll-mt-[160px]">
    <ErrorBoundary>
      <div
        v-if="title || subtitle || showMoreAsTitleLink || showMoreAsExpander"
        :class="spaciousHeader ? 'mb-16' : 'mb-3 md:mb-4'"
      >
        <div class="flex items-center justify-between gap-4">
          <Heading
            v-if="title"
            :h-class="titleHClass || `h${titleLevel}`"
            :level="titleLevel"
            :title="title"
            :use-h-tag="useHTag"
            class="mb-2 kft-product-showcase__heading kft-heading--left"
          />

          <!-- An empty `link` makes Link render a plain anchor that emits
               click:link instead of navigating, so the expander is
               indistinguishable from the navigating variant. -->
          <Link
            v-if="showMoreAsExpander"
            link=""
            class="product-showcase-title-link"
            data-test-id="show-more-expander"
            @click:link="toggleProducts"
          >
            {{
              isExpanded
                ? t('product.showcase.showLess')
                : showMoreLabel || t('product.showcase.showMore')
            }}
            &gt;
          </Link>

          <Link
            v-else-if="showMoreAsTitleLink"
            :link="localePath(buttonLink)"
            class="product-showcase-title-link"
          >
            {{ showMoreLabel || t('product.showcase.showMore') }} &gt;
          </Link>
        </div>

        <p
          v-if="subtitle"
          class="text-base font-normal text-stone-700 mt-1 max-w-[720px]"
        >
          {{ subtitle }}
        </p>
      </div>

      <ProductsLoader
        v-show="loading || !isProductsReady"
        :is-horizontal="isMobileSliderLayout"
        :products-count="visibleProducts.length || 16"
      />

      <div v-show="!loading && isProductsReady" class="flex flex-col">
        <component
          :is="productShowcaseContainer"
          class="product-showcase-cards-container"
          :with-arrows="isSlider || undefined"
          :class="{
            'product-showcase-cards-container--column': isSingleColumn,
            'product-showcase-cards-container--slider': isMobileSliderLayout,
            'product-showcase-cards-container--slider-grid':
              isMobileSliderLayout && !isSlider,
            'product-showcase-cards-container--slider-desktop': isSlider,
          }"
          :data-test-id="showcaseContainerDataTestId"
        >
          <template
            v-for="(item, i) in visibleProducts"
            :key="`kft-search-product-card-${item.objectID}`"
          >
            <div
              :class="{
                flex: !isMobileSliderLayout,
                'slider-item': isMobileSliderLayout,
                hidden: i + 1 > showItemsOnDesktop && isCollapsed,
              }"
            >
              <transition v-if="!isMobileSliderLayout" name="fade-slide-up">
                <!-- using v-show for animation effect on homepage -->
                <!-- probably can be fully removed with <transition> -->
                <ProductCard
                  v-show="
                    isMobile ||
                    !isCollapsed ||
                    (i < showItemsOnDesktop && isCollapsed)
                  "
                  v-bind="productCardProps(item, i)"
                  :is-slide="isMobileSliderLayout"
                  :before-navigation-callback="
                    () => fnHandleClickOnCard(item, i)
                  "
                  :before-same-tab-location-assign="
                    () =>
                      saveListProductScrollRestore(
                        item.objectID,
                        route.fullPath
                      )
                  "
                  @click:wishlist="handleClickWishlist(item)"
                />
              </transition>
              <ProductCard
                v-else
                v-bind="productCardProps(item, i)"
                :is-slide="isMobileSliderLayout"
                :before-navigation-callback="() => fnHandleClickOnCard(item, i)"
                :before-same-tab-location-assign="
                  () =>
                    saveListProductScrollRestore(item.objectID, route.fullPath)
                "
                @click:wishlist="handleClickWishlist(item)"
              />
            </div>

            <ProductCard
              v-if="i === 0 && specialGiftcardEvent"
              v-bind="productGiftcardProps(specialGiftcardEvent)"
              :is-slide="isMobileSliderLayout"
              is-special-giftcard
              @click:wishlist="handleClickWishlist(specialGiftcardEvent)"
            />

            <template v-for="(position, index) in promotionSlotIndices">
              <slot v-if="i === position" :name="`promotion${index + 1}`" />
            </template>
          </template>
        </component>

        <Link
          v-if="showMoreAsButton"
          :link="localePath(buttonLink)"
          class="show-more-button"
        >
          <Button class="w-full">
            {{ showMoreLabel || t('product.showcase.showMore') }}
          </Button>
        </Link>
      </div>
      <!-- End::Product List -->
    </ErrorBoundary>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, onMounted, watch, nextTick } from 'vue'
import type { InsightsClient } from 'search-insights'
import type { AlgoliaEventItem } from '~/composables/types/algolia'
import {
  sendViewItemList,
  sendSelectListItem,
} from '~/composables/useGtmEvents'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import { useSendEventToAlgolia } from '~/composables/product/useProduct'
import { useGetFormattedDuration } from '~/composables/useGetFormattedDuration'
import { useWishlistSidebarUiState } from '~/composables/useUiState'
import { useWishlist } from '~/composables/useWishlist'
import { useIntersectionObserver } from '~/composables/useIntersectionObserver'
import { algoliaEventGetters } from '~/utils/algoliaEventGetters'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import type { GtmItemListId } from '~/composables/useKftGtm/types'
import type { ProductCardImageBadge } from '~/components/product/ProductCardImage.vue'
import Link from '~/components/common/Link.vue'
import ProductsShowcaseSlider from '~/components/product/ProductsShowcaseSlider.vue'
import {
  saveListProductScrollRestore,
  tryRestoreListProductScroll,
} from '~/composables/product/useListProductScrollRestore'

const { t, locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()
const { token: userToken } = useAlgoliaSearchInsights()
const { sendViewedObjectIDs } = useAlgoliaConversion()

const { isMobile } = useDevice()
const {
  addAlgoliaEventItem,
  isAlgoliaEventItemOnWishlist,
  removeAlgoliaEventItem,
} = useWishlist()
const { toggle: toggleWishlistSidebar } = useWishlistSidebarUiState()

const emit = defineEmits(['click:product-card'])

interface Props {
  title?: string
  subtitle?: string
  /** Widens the header-to-grid gap to 64px (e.g. the CMS EventList block). */
  spaciousHeader?: boolean
  queryId?: string | string[]
  titleLevel?: number
  titleHClass?: string
  tags?: any[]
  products?: any[]
  imageLoading?: '' | 'lazy' | 'eager'
  promotionSlotIndices?: number[]
  loading?: boolean
  /**
   * Target of the "show more" control. Resolved through `localePath`, so pass
   * an unprefixed route. Renders as a button below the grid unless
   * `showMoreInTitle` moves it into the section title.
   */
  buttonLink?: string
  /**
   * Renders "show more" as a text link beside the section title instead of as
   * a button below the grid. Opt-in per page; ignored when `isSlider` is set,
   * which keeps the button.
   */
  showMoreInTitle?: boolean
  /**
   * Turns "show more" into a button that reveals the products `maxRows` /
   * `maxProducts` sliced off, instead of linking away. Only rendered while
   * something is actually hidden, and takes precedence over `buttonLink`.
   */
  expandable?: boolean
  /** Overrides the default `product.showcase.showMore` label. */
  showMoreLabel?: string
  disableSendEventToAlgolia?: boolean
  algoliaInsightsLabel?: string
  algoliaIndexName?: string
  gtmItemListId?: string | null
  algoliaInsightsClient?: InsightsClient | null
  giftCardEvent?: boolean
  showItemsOnDesktop?: number
  useHTag?: boolean
  specialGiftcardEvent?: AlgoliaEventItem
  isDesktopAccordeon?: boolean
  /**
   * Hard cap on how many products render. Combined with `maxRows`, the smaller
   * of the two wins.
   */
  maxProducts?: number
  /**
   * Caps rendered products to whole grid rows (4 columns from `md:` up, 1 when
   * `isSingleColumn`). Rail layouts have no rows, so use `maxProducts` there.
   */
  maxRows?: number
  /** Horizontal rail on mobile, 4-column grid from `md:` up. */
  isMobileSlider?: boolean
  /** Horizontal rail at every breakpoint, with drag + arrow controls on desktop. */
  isSlider?: boolean
  isDurationVisible?: boolean
  isSingleColumn?: boolean
  showcaseContainerDataTestId?: string
  refreshOnClientSide?: boolean
  /** Hide the wishlist heart — for layouts without a wishlist sidebar (e.g. `blank`). */
  disableWishlist?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  spaciousHeader: false,
  queryId: '',
  titleLevel: 2,
  titleHClass: 'text-h3 font-semibold',
  tags: () => [],
  products: () => [],
  imageLoading: 'lazy',
  promotionSlotIndices: () => [1, 9],
  loading: false,
  buttonLink: '',
  showMoreInTitle: false,
  expandable: false,
  showMoreLabel: '',
  disableSendEventToAlgolia: false,
  algoliaInsightsLabel: '',
  algoliaIndexName: undefined,
  gtmItemListId: null,
  algoliaInsightsClient: null,
  giftCardEvent: false,
  mobileHorizontalScroll: false,
  showItemsOnDesktop: 4, // works together with isDesktopAccordeon
  useHTag: true,
  specialGiftcardEvent: undefined,
  isDesktopAccordeon: false,
  maxProducts: undefined,
  maxRows: undefined,
  isMobileSlider: false,
  isSlider: false,
  isDurationVisible: true,
  isSingleColumn: false,
  showcaseContainerDataTestId: 'search-results',
  refreshOnClientSide: true,
  disableWishlist: false,
})

/**
 * "Show more" placement. The in-title text link is opt-in per page, and never
 * used for a full slider — those keep the original button below the rail.
 */
const showMoreAsExpander = computed(
  () => props.expandable && (hiddenProductCount.value > 0 || isExpanded.value)
)

const showMoreAsTitleLink = computed(
  () =>
    !showMoreAsExpander.value &&
    !!props.buttonLink &&
    props.showMoreInTitle &&
    !props.isSlider
)

/** The original default: a centred button below the grid, desktop only. */
const showMoreAsButton = computed(
  () =>
    !showMoreAsExpander.value &&
    !showMoreAsTitleLink.value &&
    ((props.products?.length > 4 && props.isDesktopAccordeon) ||
      !!props.buttonLink)
)

// SSR-safe mobile carousel: layout follows CSS breakpoints, not useDevice().
// Same HTML for every request (CloudFront cache) — mobile scroll, desktop grid at md:.
const isMobileSliderLayout = computed(
  () => props.isMobileSlider || props.isSlider
)

// Mirrors the grid in the stylesheet below: md:grid-cols-4, single column when
// isSingleColumn. Kept in sync manually — there is no CSS to read at SSR time.
const GRID_COLUMNS = 4
const productsPerRow = computed(() => (props.isSingleColumn ? 1 : GRID_COLUMNS))

// Row/product caps are a hard slice, not a CSS hide: the extra products are
// never rendered and never tracked as impressions. `isDesktopAccordeon` is a
// separate, desktop-only visual truncation that still renders every card.
// Set once the reader asks for the rest; from then on nothing is sliced off.
const isExpanded = ref(false)

const productLimit = computed(() => {
  if (isExpanded.value) return undefined

  const limits = [
    props.maxProducts,
    props.maxRows && props.maxRows * productsPerRow.value,
  ].filter((limit): limit is number => Boolean(limit))

  return limits.length ? Math.min(...limits) : undefined
})

const visibleProducts = computed(() => {
  const products = props.products ?? []

  return productLimit.value ? products.slice(0, productLimit.value) : products
})

/** How many products the row/product caps are currently holding back. */
const hiddenProductCount = computed(
  () => (props.products?.length ?? 0) - visibleProducts.value.length
)

const productShowcaseContainer = computed(() =>
  isMobileSliderLayout.value ? ProductsShowcaseSlider : 'div'
)

const headers = useRequestHeaders(['x-bot'])

// Hide loading until components are mounted
// If refreshOnClientSide is false, products are ready immediately (no loader)
// Otherwise, show loader until mount (or if bot on server)
const isProductsReady = ref(
  props.refreshOnClientSide === false
    ? true
    : import.meta.client
      ? false
      : headers['x-bot'] === 'true'
)
onMounted(() => {
  // Only delay if refreshOnClientSide is true (default behavior)
  if (props.refreshOnClientSide !== false) {
    nextTick(() => {
      isProductsReady.value = true
    })
  }
})

/**
 * Shows or hides the products the caps held back. Either way the grid changes
 * height under the reader, so bring them back to the top of this section
 * rather than leaving them stranded mid-grid.
 */
const toggleProducts = async () => {
  isExpanded.value = !isExpanded.value
  await nextTick()
  productShowcase.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const getBadges = (item: AlgoliaEventItem): ProductCardImageBadge[] => {
  const badges: ProductCardImageBadge[] = []

  const generalBadgeLabel = algoliaEventGetters.getBadgeLabel(item, t)

  if (generalBadgeLabel)
    badges.push({
      label: generalBadgeLabel,
      variant: 'default' as const,
    })

  if (algoliaEventGetters.getHasPromotions(item))
    badges.push({ label: t('product.card.sale'), variant: 'promo' as const })

  return badges
}

// Product card props in one place
const productCardProps = (item: AlgoliaEventItem, index: number) => {
  return {
    dataInsightsObjectId: item.objectID,
    duration: fnGetFormattedDuration(algoliaEventGetters.fnGetDuration(item)),
    durationTitle: t('product.card.duration'),
    minNumberOfPeople: item.min_number_of_people ?? 0,
    maxNumberOfPeople: item.max_number_of_people ?? 0,
    image: algoliaEventGetters.getPrincipalImage(item),
    imageLoading: props.imageLoading,
    next: null,
    link: algoliaEventGetters.fnGetLink(item),
    productCategory: algoliaEventGetters.fnGetMainCategory(item),
    productType: '',
    badges: getBadges(item),
    rating: algoliaEventGetters.getAverageRating(item, localeBCP47),
    isPartnerRating: algoliaEventGetters.getIsPartnerRating(item),
    isThirdPartyRating: algoliaEventGetters.getIsThirdPartyRating(item),
    regularPrice: algoliaEventGetters.getRegularPriceAmount(item),
    specialPrice: algoliaEventGetters.getSpecialPriceAmount(item),
    priceSuffix: algoliaEventGetters.getIsGroupPricing(item)
      ? t('product.card.price.groupPricingSuffix', {
          count: item.min_number_of_people,
        })
      : undefined,
    showAddToCartButton: false,
    style: { '--index': index },
    title: item.title || 'simple-event-showcase',
    alt: algoliaEventGetters.getPrincipalAlt(item),
    wishlistIcon: true,
    isOnWishlist: isAlgoliaEventItemOnWishlist(item),
    disableWishlist: props.disableWishlist,
    hasShippingCosts: algoliaEventGetters.hasShippingCosts(item),
    isDurationVisible: props.isDurationVisible,
    isTeamevent: algoliaEventGetters.getIsTeamevent(item),
    isMobileView: props.isSingleColumn,
    isBoxEvent: algoliaEventGetters.isBoxEvent(item),
    class: {
      'md:hidden': index + 1 > 4 && isCollapsed.value,
    },
    openInNewTab: !isMobile,
    qid: props.queryId,
    listProductObjectId: item.objectID,
  }
}

// Special giftcard props
const productGiftcardProps = (item: AlgoliaEventItem) => {
  return {
    dataTestId: 'product-card-giftcard',
    duration: fnGetFormattedDuration(algoliaEventGetters.fnGetDuration(item)),
    image: algoliaEventGetters.getPrincipalImage(item),
    link: `/giftcard/${algoliaEventGetters.fnGetPermalink(item)}/`,
    productCategory: algoliaEventGetters.fnGetMainCategory(item) || undefined,
    regularPrice: algoliaEventGetters.getRegularPriceAmount(item),
    specialPrice: algoliaEventGetters.getSpecialPriceAmount(item),
    rating: algoliaEventGetters.getAverageRating(item, localeBCP47),
    isPartnerRating: algoliaEventGetters.getIsPartnerRating(item),
    isThirdPartyRating: algoliaEventGetters.getIsThirdPartyRating(item),
    title: item.title || 'No title',
    wishlistIcon: true,
    isOnWishlist: isAlgoliaEventItemOnWishlist(item),
    isDurationVisible: false,
    isMobileView: props.isSingleColumn,
    openInNewTab: !isMobile,
  }
}

const fnGetFormattedDuration = (d: any) => useGetFormattedDuration(d)

const shouldRunSendViewItemList = ref(false)
const localeBCP47 = convertLocaleIntoBCP47(locale.value)

const isCollapsed = ref(props.isDesktopAccordeon)

const productShowcase = ref<HTMLElement | null>(null)

// Intersection observer for viewport-based tracking
const { intersectionHappened } = useIntersectionObserver(
  productShowcase as Ref<HTMLDivElement | undefined>
)
const hasTrackedView = ref(false)

const handleClickWishlist = (item: AlgoliaEventItem) => {
  if (isAlgoliaEventItemOnWishlist(item)) {
    removeAlgoliaEventItem(item)
  } else {
    addAlgoliaEventItem(item)
    toggleWishlistSidebar()
  }
}

watch(
  [() => props.loading, () => visibleProducts.value.length, isProductsReady],
  () => {
    if (!isMobile) {
      return
    }
    void tryRestoreListProductScroll({
      currentPath: route.fullPath,
      loading: props.loading,
      products: visibleProducts.value,
      isProductsReady: isProductsReady.value,
      isRetry: false,
    })
  },
  { flush: 'post' }
)

const fnHandleClickOnCard = (item: AlgoliaEventItem, i: number) => {
  if (!props.disableSendEventToAlgolia) {
    useSendEventToAlgolia({
      objectID: item.objectID,
      position: Number(i + 1),
      algoliaInsightsClient: props.algoliaInsightsClient,
      label: props.algoliaInsightsLabel,
      queryId: Array.isArray(props.queryId)
        ? props.queryId.join(',')
        : props.queryId,
      locale: locale.value,
      userToken: userToken.value || '',
      indexName: props.algoliaIndexName,
    })
  }

  emit('click:product-card', item)
  sendSelectListItem(props.gtmItemListId as GtmItemListId, [item], i)
}

watch(
  [visibleProducts, intersectionHappened],
  ([newProducts, hasIntersected], [oldProducts]) => {
    // Reset tracking flag when a new set of products arrives
    if (newProducts !== oldProducts) {
      hasTrackedView.value = false
    }

    // Only proceed if we have products, the component has intersected with viewport, and we haven't tracked yet
    if (
      !newProducts ||
      newProducts.length === 0 ||
      !hasIntersected ||
      hasTrackedView.value
    ) {
      return
    }

    // Send GTM view_item_list event
    if (props.gtmItemListId) {
      if (import.meta.client) {
        sendViewItemList(
          props.gtmItemListId as GtmItemListId,
          visibleProducts.value
        )
      } else {
        shouldRunSendViewItemList.value = true
      }
    }

    // Send Algolia viewedObjectIDs event
    if (props.algoliaInsightsLabel && import.meta.client) {
      sendViewedObjectIDs(
        visibleProducts.value,
        props.algoliaInsightsLabel,
        props.algoliaIndexName
      )
    }

    // Mark as tracked to prevent duplicate events
    hasTrackedView.value = true
  },
  { immediate: false }
)
</script>

<style lang="postcss">
.show-more-button {
  @apply w-[300px] h-[45px] self-center mt-4 md:mt-10 hidden md:block;
}

.product-showcase-title-link {
  @apply shrink-0 font-semibold hidden md:block text-teal-750 underline hover:no-underline;
}

.product-showcase-cards-container:not(
  .product-showcase-cards-container--slider
) {
  @apply grid grid-cols-1 gap-2 pt-1;

  &:not(.product-showcase-cards-container--column) {
    @apply md:grid-cols-4 md:gap-4;
  }
}

/* Mobile rail — shared by both slider modes. Desktop behaviour is layered on top
   by --slider-grid (collapses into the 4-column grid) or --slider-desktop
   (keeps scrolling, with arrows). */
.product-showcase-cards-container--slider {
  @apply flex flex-nowrap gap-2 overflow-x-auto snap-x snap-mandatory pt-1 -mx-4;
  scrollbar-width: none;
  scroll-padding-left: 1rem;
  scroll-padding-right: 1rem;

  /* Scrollable leading/trailing inset — aligns first card with px-4 gutter at rest,
     then scrolls away so cards move edge-to-edge (same as Swiper slidesOffsetBefore/After). */
  &::before,
  &::after {
    content: '';
    flex: 0 0 1rem;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.product-showcase-cards-container--slider-grid {
  @apply md:grid md:grid-cols-4 md:gap-4 md:overflow-visible md:snap-none md:mx-0 md:px-0;

  @media (min-width: 768px) {
    scroll-padding-left: 0;
    scroll-padding-right: 0;

    &::before,
    &::after {
      display: none;
    }
  }
}

.product-showcase-cards-container--slider-desktop {
  @apply md:gap-4 md:mx-0;

  /* Desktop scrolls via drag + arrows only (same as the event pages); native
     touch scrolling stays untouched on mobile. */
  @media (min-width: 768px) {
    overflow-x: hidden;
    scroll-snap-type: none;
    touch-action: auto;
    scroll-padding-left: 0;
    scroll-padding-right: 0;

    &::before,
    &::after {
      display: none;
    }
  }
}

.product-showcase-cards-container--slider .slider-item {
  position: relative;
  flex: 0 0 auto;
  flex-shrink: 0;
  scroll-snap-align: start;
  width: min(240px, 70vw);

  @media (max-width: 320px) {
    width: min(240px, 85vw);
  }
}

.product-showcase-cards-container--slider-grid .slider-item {
  @media (min-width: 768px) {
    width: auto;
  }
}

.product-showcase-cards-container--slider-desktop .slider-item {
  @media (min-width: 768px) {
    /* Exactly four cards span the container — three 1rem gaps (md:gap-4)
       between them — so the fourth card's right edge lands flush with the
       container edge at any page width, however many cards follow. Four also
       matches the md:grid-cols-4 layout the non-slider showcase uses. A fixed
       width cannot do this: where the fourth card ends would depend on the
       container, leaving it either cut off or short of the edge. */
    width: calc((100% - 3rem) / 4);
  }
}

@media (max-width: 767px) {
  .product-showcase-cards-container--slider .slider-item .product-card-wrapper {
    @apply h-auto w-full;
  }

  .product-showcase-cards-container--slider .slider-item .product-card {
    @apply flex-col pr-2;
  }

  .product-showcase-cards-container--slider
    .slider-item:last-child
    .product-card {
    @apply pr-0;
  }

  .product-showcase-cards-container--slider
    .slider-item
    .product-card__content {
    width: 100%;
    @apply p-0 pt-1.5;
  }

  .product-showcase-cards-container--slider .slider-item .card-image-wrapper {
    width: 100%;
    height: 180px;
  }
}

/* Desktop rail keeps the stacked card the mobile rail uses, with a taller image
   — matches EventHorizontalProductShowcase on the event pages. */
@media (min-width: 768px) {
  .product-showcase-cards-container--slider-desktop
    .slider-item
    .product-card-wrapper {
    @apply h-auto w-full;
  }

  .product-showcase-cards-container--slider-desktop .slider-item .product-card {
    @apply flex-col;
  }

  .product-showcase-cards-container--slider-desktop
    .slider-item
    .product-card__content {
    width: 100%;
    @apply px-1 pt-1.5 pl-0;
  }

  .product-showcase-cards-container--slider-desktop
    .slider-item
    .card-image-wrapper {
    width: 100%;
    height: 222px;
  }
}

.fade-slide-up-enter-active,
.fade-slide-up-leave-active {
  transition: transform 0.3s ease;
}

.fade-slide-up-enter-from,
.fade-slide-up-leave-to {
  transform: translateY(30px);
  opacity: 0;
}

.fade-slide-up-leave-active {
  position: absolute;
}
</style>
